// ── Intranet ──
import { lazyPage, lazySharedLib, lazySharedLibPage, lazyPageWithComponents } from './lazyPage.js'
// Bare shared-library import — read off `_wsShared.pages` only inside a
// deferred loader (never at module scope): a deep
// '@symbo-ls/workspace-shared/pages/auth/login.js' subpath cannot resolve
// through the platform's public importmap once this project is pushed as a
// library (tickets/execution-board.md §1), and a direct module-scope read is
// a stub-phantom-value under frank (WS-SHARED-LAZYPAGE-HOLLOW-ROUTES-SWITCH-1
// — see ./lazyPage.js's header for the mechanism).
import _wsShared from '@symbo-ls/workspace-shared'
import { main } from './main.js'
import { workspaceHub } from './workspaceHub.js'
import { people } from './people.js'
import { my } from './my.js'
import { announcements } from './announcements.js'
import { company } from './company.js'
// /data (~99KB dev) + /add-app — admin-grade surfaces, route-map-only (no
// bare-key consumers): lazy per bundle-split T1.
// Its Data* row components fold in on first mount (lazyPageWithComponents).
const data = lazyPageWithComponents(
  () => import('./data.js'),
  'data',
  (el) => ({
    title: el.call('pageTitle', 'workspace.page.data')
  })
)
const addApp = lazyPage(
  () => import('./add-app.js').then((m) => m.addApp),
  (el) => ({
    title: el.call('pageTitle', 'workspace.page.addApp')
  })
)
// /data/external — the Data backend-visualization surface (connected org
// backends + records-plane browser + fetch wiring), flag-gated by
// `data.externalSources`. Selection is state-driven (root `_ext*` keys) on
// this single registered path — source/table picks ride ?src=&table= query
// params so the shared RemoteTableCard's router navigation never falls
// through to the '/*' catch-all.
const dataExternal = lazyPage(
  () => import('./data-external.js').then((m) => m.dataExternal),
  (el) => ({
    title: el.call('pageTitle', 'workspace.page.dataExternal')
  })
)
// /reminders — built-in personal reminders app (workspace-shell owns it,
// not a feature package). Lazy per the same bundle-split convention as the
// other operator/secondary surfaces on this route map.
const reminders = lazyPage(
  () => import('./reminders.js').then((m) => m.reminders),
  (el) => ({
    title: el.call('pageTitle', 'workspace.page.reminders')
  })
)
// THE APP SPLIT (docs/APP_SPLIT.md) — the Workspace apps view of the Apps hub.
const appsHub = lazyPage(
  () => import('./apps.js').then((m) => m.appsHub),
  (el) => ({
    title: el.call('pageTitle', 'workspace.page.apps')
  })
)
// /history — built-in History app: the workspace activity log as a
// horizontal timeline (workspace-shell page, not a feature package). Lazy
// like the other built-in app pages.
const history = lazyPage(
  () => import('./history.js').then((m) => m.history),
  (el) => ({
    title: el.call('pageTitle', 'workspace.page.history')
  })
)
// /getting-started/team — POB S9 "Invite your team" (the people the
// onboarding research found on the company's own team page). Opened from the
// first-thread Team card through openInSlot; lazy like the other built-in
// pages.
const gettingStartedTeam = lazyPage(
  () => import('./gettingStartedTeam.js').then((m) => m.gettingStartedTeam),
  (el) => ({
    title: el.call('pageTitle', 'workspace.gettingStarted.team.title')
  })
)
// /getting-started/imports — POB S10 "Import your data" (the import
// connectors: the owner's own file, and the others "Coming soon" until they
// are live). `?source=<id>` opens a live connector's review.
const gettingStartedImports = lazyPage(
  () => import('./gettingStartedImports.js').then((m) => m.gettingStartedImports),
  (el) => ({
    title: el.call('pageTitle', 'workspace.gettingStarted.imports.title')
  })
)
// /inbox — full-page notification center. The bell panel's "View all
// notifications" + NotificationBell have routed here all along while the
// page itself was missing (manifest reserved the route; the router
// 404-bounced every click).
const inbox = lazyPage(
  () => import('./inbox.js').then((m) => m.inbox),
  (el) => ({
    title: el.call('pageTitle', 'workspace.page.inbox')
  })
)
// /team-and-agents is a panzoom canvas page — it lives in
// @symbo-ls/workspace-canvas next to /studios. The route below reaches it by
// STRING-EXTEND (`extends: 'teamAndAgents'`), never by importing the sibling
// page object: canvas's eager slice (sharedLibraries.js `canvasEager`)
// already registers `teamAndAgents` as a component at boot, and a value
// imported from a declared library serializes to "" when this project is
// published (frank stub-phantom) — the platform record's /team-and-agents
// would have gone hollow on the next push and shadowed canvas's real page
// (measured by the prod-platform-libraries frank gate, 2026-09-22).
// /agents is the agents-only view of the team-and-agents canvas (no people,
// + a My/All/Status/Type/Assignment filter toolbar). It replaces /my-agents,
// which now aliases to it.
const agents = lazyPage(
  () => import('@symbo-ls/workspace-canvas/pages/agents.js').then((m) => m.agents),
  (el) => ({ title: el.call('pageTitle', 'workspace.page.agents') })
)

// ── Analytics ── (lazy shared library — project dashboard + workspace usage +
// map + logs). @symbo-ls/workspace-analytics is deferred out of the eager
// `sharedLibraries.js` merge — its ~74K of dashboard/logs components + rollup/
// logs/map functions + route table only matter once an analytics route is
// visited. Its context.js carries a `.pages` map, so each route registers via
// lazySharedLib: on first mount it folds analytics' components + functions +
// state into the live shell context, then el.sets the picked page (each is
// itself a lazyPage that loads its own body chunk). The EAGER carve-out is the
// analytics state schema alone (sharedLibraries.js), for the canvas
// dashboard's Analytics button, which writes analytics root keys before any
// analytics route is visited. Bare /logs stays a shell redirect
// (logsRedirect) — see the route map below.
// /s/:token — the public share-link viewer (PUBLIC-SHARE-LINKS-1). Lazy:
// a recipient-only surface with no bare-key consumers anywhere in the app.
const sharedResource = lazyPage(
  () => import('./sharedResource.js').then((m) => m.sharedResource),
  (el) => ({
    title: el.call('polyglot', 'workspace.page.sharedResource')
  })
)
import { logsRedirect } from './logs-redirect.js'
import { tasksRedirect } from './tasks-redirect.js'
import { notesRedirect } from './notes-redirect.js'
const _analyticsCtx = () => import('@symbo-ls/workspace-analytics')
const analyticsProject = lazySharedLib(
  _analyticsCtx,
  (lib) => lib.pages['/analytics'],
  (el) => ({
    title: el.call('pageTitle', 'workspace.page.analytics')
  })
)
const workspaceAnalytics = lazySharedLib(
  _analyticsCtx,
  (lib) => lib.pages['/workspace-analytics'],
  (el) => ({
    title: el.call('pageTitle', 'workspace.page.workspaceAnalytics')
  })
)
const analyticsMap = lazySharedLib(
  _analyticsCtx,
  (lib) => lib.pages['/analytics/map'],
  (el) => ({
    title: el.call('pageTitle', 'workspace.page.analyticsMap')
  })
)
const analyticsSessions = lazySharedLib(
  _analyticsCtx,
  (lib) => lib.pages['/analytics/sessions'],
  (el) => ({
    title: el.call('pageTitle', 'workspace.page.analyticsSessions')
  })
)
// Fixed-path session detail (PageSlot drill-through target — id rides
// root._logsSessionId; the /sessions/:id param route stays for real URLs).
const analyticsLogsSessionFixed = lazySharedLib(
  _analyticsCtx,
  (lib) => lib.pages['/analytics/logs/session'],
  (el) => ({ title: el.call('pageTitle', 'workspace.page.logsSession') })
)
// PROJECTS-family per-session detail + bugs / events views
// (WS-ANALYTICS-FAMILY-SPLIT-UTC-TOTALS-1) — the same page modules as the
// workspace logs sections, scoped by family at fetch time inside the
// analytics package. Existing polyglot titles — no new i18n keys.
const analyticsSession = lazySharedLib(
  _analyticsCtx,
  (lib) => lib.pages['/analytics/sessions/:id'],
  (el) => ({ title: el.call('pageTitle', 'workspace.page.logsSession') })
)
const analyticsSessionFixed = lazySharedLib(
  _analyticsCtx,
  (lib) => lib.pages['/analytics/session'],
  (el) => ({ title: el.call('pageTitle', 'workspace.page.logsSession') })
)
const analyticsBugs = lazySharedLib(
  _analyticsCtx,
  (lib) => lib.pages['/analytics/bugs'],
  (el) => ({ title: el.call('pageTitle', 'workspace.page.analyticsBugs') })
)
const analyticsEvents = lazySharedLib(
  _analyticsCtx,
  (lib) => lib.pages['/analytics/events'],
  (el) => ({ title: el.call('pageTitle', 'workspace.page.analyticsEvents') })
)
// Unique visitors (WS-ANALYTICS-UTM-CAMPAIGNS-UI-1 addendum) — the logs
// Users module on the projects family, its own title.
const analyticsVisitors = lazySharedLib(
  _analyticsCtx,
  (lib) => lib.pages['/analytics/visitors'],
  (el) => ({ title: el.call('pageTitle', 'workspace.page.analyticsVisitors') })
)
const analyticsLogs = lazySharedLib(
  _analyticsCtx,
  (lib) => lib.pages['/workspace-analytics/logs'],
  (el) => ({
    title: el.call('pageTitle', 'workspace.page.logs')
  })
)
const analyticsLogsUsers = lazySharedLib(
  _analyticsCtx,
  (lib) => lib.pages['/workspace-analytics/logs/users'],
  (el) => ({ title: el.call('pageTitle', 'workspace.page.logsUsers') })
)
const analyticsLogsSessions = lazySharedLib(
  _analyticsCtx,
  (lib) => lib.pages['/workspace-analytics/logs/sessions'],
  (el) => ({ title: el.call('pageTitle', 'workspace.page.logsSessions') })
)
const analyticsLogsSession = lazySharedLib(
  _analyticsCtx,
  (lib) => lib.pages['/workspace-analytics/logs/sessions/:id'],
  (el) => ({ title: el.call('pageTitle', 'workspace.page.logsSession') })
)
const analyticsLogsEvents = lazySharedLib(
  _analyticsCtx,
  (lib) => lib.pages['/workspace-analytics/logs/events'],
  (el) => ({ title: el.call('pageTitle', 'workspace.page.logsEvents') })
)
const analyticsLogsBugs = lazySharedLib(
  _analyticsCtx,
  (lib) => lib.pages['/workspace-analytics/logs/bugs'],
  (el) => ({ title: el.call('pageTitle', 'workspace.page.logsBugs') })
)
const analyticsLogsNetwork = lazySharedLib(
  _analyticsCtx,
  (lib) => lib.pages['/workspace-analytics/logs/network'],
  (el) => ({ title: el.call('pageTitle', 'workspace.page.logsNetwork') })
)
const analyticsLogsVerbose = lazySharedLib(
  _analyticsCtx,
  (lib) => lib.pages['/workspace-analytics/logs/verbose'],
  (el) => ({ title: el.call('pageTitle', 'workspace.page.logsVerbose') })
)

// ── Identity pages (consolidated in shared/pages/) ──
// Imported from their SOURCE files, deliberately NOT via the
// identity-index.js barrel: that barrel also statically imports all 16
// admin pages, so a single static barrel import pinned the whole admin
// surface (~192KB dev) into the eager shell chunk. Auth pages must stay
// eager (signed-out boots render them first paint); admin lazifies below.
// (Bundle-split T1 — see tickets/workspace.md "iPad/native-like program".)
// Read off the bare `_wsShared` import (page defs keyed by export name in
// shared's pages namespace — `callback` is already aliased to `authCallback`
// by shared's own barrel), deferred inside a `lazyPage` loader — never at
// module scope. Deep per-file subpaths like
// '@symbo-ls/workspace-shared/pages/auth/login.js' cannot resolve through
// the platform's public importmap once this project is pushed as a library
// (tickets/execution-board.md §1), and a direct module-scope read is a
// stub-phantom-value under frank (WS-SHARED-LAZYPAGE-HOLLOW-ROUTES-SWITCH-1).
// `@symbo-ls/workspace-shared` rides the shell's eager sharedLibraries merge,
// so `_wsShared.pages` is already populated by the time any of these mount —
// the loader's `Promise.resolve` settles on the next microtask, before the
// next paint, so first-paint stays effectively eager (same trick already
// proven live for acceptInvite/waitlistInvite below).
// On a white-label host these two wrappers yield the title while the brand
// loads (and to the inner page once it has a name) — functions/authWrapperTitle.js.
const login = lazyPage(
  () => Promise.resolve(_wsShared.pages.login),
  (el) => ({ title: el.call('authWrapperTitle', 'workspace.page.login') })
)
const signin = lazyPage(
  () => Promise.resolve(_wsShared.pages.signin),
  (el) => ({
    title: el.call('authWrapperTitle', 'workspace.page.signin')
  })
)
const signup = lazyPage(
  () => Promise.resolve(_wsShared.pages.signup),
  (el) => ({
    title: el.call('authWrapperTitle', 'workspace.page.signup')
  })
)
const signupConfirmation = lazyPage(
  () => Promise.resolve(_wsShared.pages.signupConfirmation),
  (el) => ({
    title: el.call('polyglot', 'workspace.page.signupConfirmation')
  })
)
const resetPassword = lazyPage(
  () => Promise.resolve(_wsShared.pages.resetPassword),
  (el) => ({
    title: el.call('polyglot', 'workspace.page.resetPassword')
  })
)
const authCallback = lazyPage(
  () => Promise.resolve(_wsShared.pages.authCallback),
  (el) => ({
    title: el.call('polyglot', 'workspace.page.authCallback')
  })
)
const magicCallback = lazyPage(
  () => Promise.resolve(_wsShared.pages.magicCallback),
  (el) => ({
    title: el.call('polyglot', 'workspace.page.authCallback')
  })
)
// /profile and /orgs: the page's OWN title logic, asked at call time
// (shared/pages/profile.js metadata, "<name> / People / <brand>"), read inside
// the function, never at module scope (the rule at the top of this file). An
// open pane titles itself from the wrapper's metadata (below), so a fixed
// workspace.page.profile turned a member's profile pane into "Account"
// (dev.my 2026-10-30).
const profile = lazyPage(
  () => Promise.resolve(_wsShared.pages.profile),
  (el, s) => _wsShared.pages.profile.metadata(el, s)
)
// /settings mounts the same account page under its OWN title. A wrapper's
// `metadata` outlives the first mount: an open pane titles itself from it
// (canvas PageSlot _syncSlotTitle → this route's metadata, again on a pane
// switch and on a language switch; the pane's title bar reads it too). Under
// a workspace.page.profile title the Settings pane read "Account" (dev.my
// 2026-10-30). A full-page route keeps the page's own title after a language
// switch. The key is the page's own settings title (shared/pages/profile.js
// metadata, the settings view).
const accountSettings = lazyPage(
  () => Promise.resolve(_wsShared.pages.profile),
  (el) => ({
    title: el.call('pageTitle', 'account.profile.settingsTitle')
  })
)

// ── Invitation accept surfaces ── (P0 invite-flow fix, 2026-08-06)
// The server's invite emails link buildInviteUrl targets — /accept-invite
// (project), /accept-org-invite, /accept-team-invite,
// /accept-workspace-invite — which were UNROUTED here (dead-end 404 for
// every recipient in every environment). All four mount ONE shared page
// that parses kind+token, bounces unauthed visitors to /login?next=<self>
// (token preserved), and calls the matching SDK accept method.
// /waitlist-invite forwards to /signup with the token intact (signup
// already consumes ?token as sdk.register's inviteToken). Still route-time
// deferred (lazyPage), but the loader now reads the page off the bare
// `_wsShared` import instead of a deep
// '@symbo-ls/workspace-shared/pages/auth/accept-invite.js' dynamic import —
// unresolvable through the platform's public importmap once this project is
// pushed as a library (tickets/execution-board.md §1). Both pages are
// re-exported by shared's pages barrel for exactly this consumer, which
// trades their former lazy-chunk split for correctness on the platform:
// they now ride shared's eager pages barrel like every other auth page.
const acceptInvite = lazyPage(
  () => Promise.resolve(_wsShared.pages.acceptInvite),
  (el) => ({
    title: el.call('authWrapperTitle', 'workspace.page.acceptInvite')
  })
)
const waitlistInvite = lazyPage(
  () => Promise.resolve(_wsShared.pages.waitlistInvite),
  (el) => ({
    title: el.call('polyglot', 'workspace.page.waitlistInvite')
  })
)

// Public-facing user profile (the same page mounts at `/user/:slug`
// and the compact alias `/u/:slug`).
import { userProfile } from './userProfile.js'

// ── Calendar ── (lazy shared library + page — #16 calendar slim)
// @symbo-ls/workspace-calendar is deferred out of the eager `sharedLibraries.js`
// merge: its ~200KB of week/month/team/team-now/mini/sidebar/main/quick-create
// components + its 31KB page module only matter once the /calendar route is
// visited. The route is registered as a lazySharedLibPage entry: on first mount it
// folds the calendar CONTEXT (components + functions + state) into the live shell
// context (add-missing — never clobbering the eager modal slice), THEN loads the
// page module and el.sets it — so the page's bare-key children (CalMain,
// CalSidebar, CalWeekView, CalMonthView, CalTeamView, CalTeamNowView,
// CalMiniCalendar, CalQuickCreate) and every el.call resolve against the
// now-populated registry. The EAGER carve-out (CalEventDetailModal +
// CalProfileModal + CalNewBookingModal, the save/delete/move/freshBooking
// functions, and the full calendar state schema) stays registered via
// sharedLibraries.js for the home widgets / shell-root event+profile openers /
// chat / meet surfaces that reference them before the calendar route mounts.
// calendar's context.js default export has no `.pages` map (its page lives in a
// separate module), so — like /documents and /tickets — the page module is passed
// as the explicit second loader. The single _calendarCtx import is module-cached,
// so the context + page each resolve as one cached chunk.
const _calendarCtx = () => import('@symbo-ls/workspace-calendar/context.js')
const calendar = lazySharedLibPage(
  _calendarCtx,
  () => import('@symbo-ls/workspace-calendar/pages/index.js').then((m) => m.calendarHome),
  (el) => ({ title: el.call('pageTitle', 'workspace.page.calendar') })
)

// ── Tickets ── (lazy shared library + page — #16 tickets slim)
// @symbo-ls/workspace-tickets is deferred out of the eager `sharedLibraries.js`
// merge: its ~620KB of board/kanban/triage/roadmap/resolutions components +
// function surface only matter once a /tickets* or /resolutions* route is
// visited. Each route below is registered as a lazySharedLibPage entry: on first
// mount it folds the tickets CONTEXT (components + functions + state) into the
// live shell context (add-missing — never clobbering the eager slice), THEN loads
// the picked page module and el.sets it — so the page's bare-key children
// (TicketsKanban, TicketsGeneralMain, TicketsMyViewMain, ResolutionsSidebar, …)
// and every el.call resolve against the now-populated registry. The EAGER
// carve-out (TicketsDetailModal + TicketsActivityHeatmap + TicketsSidebar/
// TicketsSidebarGroup, and the save/delete/move/reloadTickets/freshTicket
// functions, and the full tickets state schema) stays registered via
// sharedLibraries.js for the home widgets / /tickets/status / chat / meet / canvas
// / infra / GreenIG surfaces that reference them before any tickets route mounts.
// tickets' context.js default export has no `.pages` map (its pages live in
// separate modules), so — like /documents and /files — the page module is passed
// as the explicit second loader. The single _ticketsCtx import is module-cached,
// so every tickets route shares ONE context chunk; each page module is its own
// cached chunk.
const _ticketsCtx = () => import('@symbo-ls/workspace-tickets/context.js')
const tickets = lazySharedLibPage(
  _ticketsCtx,
  () => import('@symbo-ls/workspace-tickets/pages/index.js').then((m) => m.myView),
  (el) => ({ title: el.call('pageTitle', 'workspace.page.myTasks') })
)
const ticketView = lazySharedLibPage(
  _ticketsCtx,
  () => import('@symbo-ls/workspace-tickets/pages/view.js').then((m) => m.ticketView),
  (el) => ({ title: el.call('pageTitle', 'workspace.page.ticket') })
)
const ticketsBoard = lazySharedLibPage(
  _ticketsCtx,
  () => import('@symbo-ls/workspace-tickets/pages/board.js').then((m) => m.home),
  (el) => ({ title: el.call('pageTitle', 'workspace.page.ticketsBoard') })
)
const ticketsGeneral = lazySharedLibPage(
  _ticketsCtx,
  () => import('@symbo-ls/workspace-tickets/pages/general.js').then((m) => m.general),
  (el) => ({ title: el.call('pageTitle', 'workspace.page.ticketsGeneral') })
)
const ticketsFeed = lazySharedLibPage(
  _ticketsCtx,
  () => import('@symbo-ls/workspace-tickets/pages/feed.js').then((m) => m.feed),
  (el) => ({ title: el.call('pageTitle', 'workspace.page.ticketsFeed') })
)
// /tickets/canvas removed (2026-06-09 per Nika) — /tickets is the board now.
// Epics + Stories merged into one /tickets/epics view (2026-06-09 per Nika);
// /tickets/logs removed.
const ticketsEpics = lazySharedLibPage(
  _ticketsCtx,
  () => import('@symbo-ls/workspace-tickets/pages/epics.js').then((m) => m.epics),
  (el) => ({ title: el.call('pageTitle', 'workspace.page.ticketsEpics') })
)
const ticketsStandups = lazySharedLibPage(
  _ticketsCtx,
  () => import('@symbo-ls/workspace-tickets/pages/standups.js').then((m) => m.standups),
  (el) => ({ title: el.call('pageTitle', 'workspace.page.ticketsStandups') })
)
const requests = lazySharedLibPage(
  _ticketsCtx,
  () => import('@symbo-ls/workspace-tickets/pages/requests.js').then((m) => m.requests),
  (el) => ({ title: el.call('pageTitle', 'workspace.page.resolutionsRequests') })
)
const requestsInbound = lazySharedLibPage(
  _ticketsCtx,
  () => import('@symbo-ls/workspace-tickets/pages/requests.js').then((m) => m.requestsInbound),
  (el) => ({ title: el.call('pageTitle', 'workspace.page.resolutionsInbound') })
)
const requestsOutbound = lazySharedLibPage(
  _ticketsCtx,
  () => import('@symbo-ls/workspace-tickets/pages/requests.js').then((m) => m.requestsOutbound),
  (el) => ({ title: el.call('pageTitle', 'workspace.page.resolutionsOutbound') })
)
const requestsAudit = lazySharedLibPage(
  _ticketsCtx,
  () => import('@symbo-ls/workspace-tickets/pages/requests.js').then((m) => m.requestsAudit),
  (el) => ({ title: el.call('pageTitle', 'workspace.page.resolutionsAudit') })
)
const resolutions = lazySharedLibPage(
  _ticketsCtx,
  () => import('@symbo-ls/workspace-tickets/pages/resolutions.js').then((m) => m.resolutions),
  (el) => ({ title: el.call('pageTitle', 'workspace.page.resolutions') })
)
const resolutionsImprovements = lazySharedLibPage(
  _ticketsCtx,
  () =>
    import('@symbo-ls/workspace-tickets/pages/resolutions.js').then(
      (m) => m.resolutionsImprovements
    ),
  (el) => ({ title: el.call('pageTitle', 'workspace.page.resolutionsImprovements') })
)
const resolutionsEditRequests = lazySharedLibPage(
  _ticketsCtx,
  () =>
    import('@symbo-ls/workspace-tickets/pages/resolutions.js').then(
      (m) => m.resolutionsEditRequests
    ),
  (el) => ({ title: el.call('pageTitle', 'workspace.page.resolutionsEditRequests') })
)
const ticketsRelease = lazySharedLibPage(
  _ticketsCtx,
  () => import('@symbo-ls/workspace-tickets/pages/release.js').then((m) => m.release),
  (el) => ({ title: el.call('pageTitle', 'workspace.page.ticketsRelease') })
)
const ticketsRoadmap = lazySharedLibPage(
  _ticketsCtx,
  () => import('@symbo-ls/workspace-tickets/pages/roadmap.js').then((m) => m.roadmap),
  (el) => ({ title: el.call('pageTitle', 'workspace.page.ticketsRoadmap') })
)

// ── Chat ── (lazy shared library + page — chat bundle-fold, 2026-08-01)
// chat is deferred out of the eager sharedLibraries.js merge (only a slim
// function slice + state stay eager); a direct /chat visit must fold the
// context FIRST so the page's bare-key children resolve — same shape as
// meet/calendar/documents. The context import is module-cached, so the
// messenger's ensureChatContext and this route share ONE chunk.
const chat = lazySharedLibPage(
  () => import('@symbo-ls/workspace-chat/context.js'),
  () => import('@symbo-ls/workspace-chat/pages/index.js').then((m) => m.chatHome),
  (el) => ({ title: el.call('pageTitle', 'workspace.page.chat') })
)

// ── Mail ── (lazy shared library + page — the built-in email client,
// architecture/MAIL.md). Same shape as chat: only a slim eager slice (three
// page-body components + the drawer toggles + state) rides sharedLibraries.js;
// a direct /mail visit folds the full context FIRST so the page's bare-key
// children resolve. The context import is module-cached, so the header
// envelope's ensureMailContext (functions/mailFold.js) and this route share
// ONE chunk.
const mail = lazySharedLibPage(
  () => import('@symbo-ls/workspace-mail/context.js'),
  () => import('@symbo-ls/workspace-mail/pages/index.js').then((m) => m.mailHome),
  (el) => ({ title: el.call('pageTitle', 'workspace.page.mail') })
)

// ── Documents ── (lazy shared library + page)
// @symbo-ls/workspace-documents is deferred out of the eager `sharedLibraries.js`
// merge — its ~65K of page-body components, fetch blocks, and search utils only
// matter on /documents. lazySharedLibPage folds the documents CONTEXT
// (components + functions + state) into the live shell context on first mount,
// THEN loads the picked page module; all four routes share the one context
// import + their own page chunk. The EAGER carve-out (DocsSidebar closure +
// createNote/createKnowledgeArticle/saveKbArticle) stays registered via
// sharedLibraries.js because shared/chat/meet/AI surfaces reference them BEFORE
// /documents is ever visited. The context.js default export has no `.pages`, so
// the page module is passed as the explicit second loader.
const documents = lazySharedLibPage(
  () => import('@symbo-ls/workspace-documents'),
  () => import('@symbo-ls/workspace-documents/pages/index.js').then((m) => m.documentsHome),
  (el) => ({ title: el.call('pageTitle', 'workspace.page.notes') })
)
const documentsKb = lazySharedLibPage(
  () => import('@symbo-ls/workspace-documents'),
  () => import('@symbo-ls/workspace-documents/pages/knowledgeBase.js').then((m) => m.knowledgeBase),
  (el) => ({ title: el.call('pageTitle', 'workspace.page.kb') })
)
const documentsEditor = lazySharedLibPage(
  () => import('@symbo-ls/workspace-documents'),
  () => import('@symbo-ls/workspace-documents/pages/editor.js').then((m) => m.editor),
  (el) => ({ title: el.call('pageTitle', 'workspace.page.editor') })
)

// ── Files ── (lazy shared library + page)
// @symbo-ls/workspace-files is deferred out of the eager `sharedLibraries.js`
// merge — its ~105K of desktop components + grid/upload/viewer functions only
// matter on /files. lazySharedLibPage folds the files CONTEXT (components +
// functions + state) into the live shell context on first mount, THEN loads the
// page module. The EAGER carve-out (createCanvasFolder / commitRenameNode /
// deleteCanvasNode / moveNodesIntoFolder) stays registered via sharedLibraries.js
// because the workspace AI layer el.call's them BEFORE /files is ever visited.
// The context.js default export has no `.pages`, so the page module is passed as
// the explicit second loader (mirrors /documents).
const files = lazySharedLibPage(
  () => import('@symbo-ls/workspace-files'),
  () => import('@symbo-ls/workspace-files/pages/files.js').then((m) => m.filesHome),
  (el) => ({ title: el.call('pageTitle', 'workspace.page.files') })
)

// ── Infra ── (lazy shared library)
// @symbo-ls/workspace-infra is a HARD LEAF — no other package string-extends,
// bare-keys, or el.calls any Infra* component/fn — so it is deferred out of the
// eager `sharedLibraries.js` merge and registered here as lazy routes instead.
// On first mount of one of these, lazySharedLib folds infra's components +
// functions into the live shell context (the injectPageInApp mechanism) and
// then renders the picked page; both routes share one dynamic-import chunk.
const infra = lazySharedLib(
  () => import('@symbo-ls/workspace-infra'),
  (lib) => lib.pages['/infra'],
  (el) => ({ title: el.call('pageTitle', 'workspace.page.infra') })
)
const infraStatus = lazySharedLib(
  () => import('@symbo-ls/workspace-infra'),
  (lib) => lib.pages['/infra/status'],
  (el) => ({ title: el.call('pageTitle', 'workspace.page.infraStatus') })
)

// ── Marketplace ── (lazy shared library)
// @symbo-ls/workspace-marketplace is deferred out of the eager shell bundle
// (#16): its ~274KB component barrel + ~116KB function barrel only matter once a
// /marketplace* route is visited. The shell used to boot-merge them via
// workspace/context.js (`...marketplacePages` + the full comp/fn barrels), which
// is exactly why a naive barrel-carve would have un-registered the whole
// MarketplaceIsland tree with no backstop. Instead every /marketplace* route is
// registered here as a lazySharedLib entry: on first mount it folds the FULL
// marketplace context (components + functions + state) into the live shell
// context, THEN el.sets the picked page — so its bare-key children
// (MarketplaceIsland, MarketplaceFeedIsland, PromptGalleryCard, …) and every
// el.call resolve against the now-populated registry. The EAGER carve-out (the
// shell-root install modal: MarketplaceInstallModal + IntegrationLogo +
// closeInstallModal) stays registered via workspace/context.js's eagerSlice
// imports. marketplace's context.js carries a `.pages` map, so each route picks
// its page off `lib.pages[...]`; all routes share one dynamic-import chunk.
const _marketplaceCtx = () => import('@symbo-ls/workspace-marketplace/context.js')
// /marketplace serves the catalogue's App Store page (decision 7, ledger AS-3):
// a bare visit opens the App Store, which moves to /apps/store
// (`marketplaceAppStoreRedirect`); a developer entry — `?surface=market` /
// `?surface=integrations` — stays here with the store's three surfaces.
const marketplace = lazySharedLib(
  _marketplaceCtx,
  (lib) => lib.pages['/apps/store'],
  (el) => ({
    title: el.call('pageTitle', 'workspace.page.marketplace')
  })
)
// /apps/store — THE ONE CATALOGUE's App Store tab (Nika's decision 7, "one
// catalogue": /apps + an App Store tab). The navbar's "Apps" opens /apps; its
// App Store tab opens this. Same lazy store context as /marketplace.
const appsStore = lazySharedLib(
  _marketplaceCtx,
  (lib) => lib.pages['/apps/store'],
  (el) => ({
    title: el.call('pageTitle', 'shell.launcher.appStore')
  })
)
const marketplaceFeed = lazySharedLib(
  _marketplaceCtx,
  (lib) => lib.pages['/marketplace/feed'],
  (el) => ({
    title: el.call('pageTitle', 'workspace.page.marketplaceCommunity')
  })
)
const marketplaceRailDemo = lazySharedLib(
  _marketplaceCtx,
  (lib) => lib.pages['/marketplace/installable-rail-demo'],
  (el) => ({ title: el.call('pageTitle', 'workspace.page.marketplaceRailDemo') })
)
const marketplacePromptGallery = lazySharedLib(
  _marketplaceCtx,
  (lib) => lib.pages['/marketplace/prompt-gallery'],
  (el) => ({ title: el.call('pageTitle', 'workspace.page.marketplacePrompts') })
)

// ── Preview ── (lazy shared library — #16 preview half)
// @symbo-ls/workspace-preview is deferred out of the eager shell bundle (#16):
// its ~121KB component barrel (Scene/SceneTemplate/SceneNavbar/PreviewNavbar/
// PreviewSidebar/PreviewAssistant/PreviewErrorBoundary) + the page + function
// barrels only matter once /preview is visited. The shell used to boot-merge
// them via workspace/context.js (`...previewPages` + the full comp/fn barrels),
// which is exactly why a naive barrel-carve would have un-registered the whole
// preview UI with no backstop. Registered here as a lazySharedLib entry instead:
// on first mount it folds the FULL preview context (components + functions) into
// the live shell context, THEN el.sets the picked page — so Scene's bare-key
// children + el.call('renderProject')/el.call('renderDesignSystem') (the iframe
// boot) resolve against the now-populated registry. preview's context.js carries
// a `.pages` map, so the route picks its page off `lib.pages['/preview']`; the
// context + its page module ride one dynamic-import chunk. The EAGER carve-out is
// only the Simone-Chat picker/model backstop fns (workspace/context.js #16). The
// home/canvas PageSlot opens the same page via _APP_WINDOW_PAGES._previewPage
// (pages/main.js), which folds this same context first.
const _previewCtx = () => import('@symbo-ls/workspace-preview/context.js')
const preview = lazySharedLib(
  _previewCtx,
  (lib) => lib.pages['/preview'],
  (el) => ({
    title: el.call('pageTitle', 'workspace.page.preview')
  })
)

// ── Meet ── (lazy shared library + page — meet+chat bundle-fold, 2026-08-01)
// @symbo-ls/workspace-meet is deferred out of the eager `sharedLibraries.js`
// merge: its ~280KB of call/room-list/video/transcript/suggestion UI +
// LiveKit/realtime function surface only matter once a /meet or /meet-guest
// route is visited. Both routes are registered as lazySharedLibPage entries:
// on first mount they fold the meet CONTEXT (components + functions) into the
// live shell context (add-missing — never clobbering the eager slice), THEN
// load the picked page module and el.set it — so the page's bare-key children
// and every el.call resolve against the now-populated registry. The EAGER
// carve-out (MeetCallWidget + MeetControlBarIconButton + MeetNewRoomModal,
// and the connectCall/disconnectCall/openNewRoomModal/closeNewRoomModal/
// createRoom/meetRoomUrl/markSuggestionApplied functions) stays registered via
// sharedLibraries.js for the shell-root call dock / global "New meeting"
// action / chat / calendar / tickets surfaces that reference them before
// either meet route ever mounts. meet's context.js default export has no
// `.pages` map (its pages live in separate modules), so — like calendar — the
// page module is passed as the explicit second loader. The single _meetCtx
// import is module-cached, so both meet routes share ONE context chunk.
const _meetCtx = () => import('@symbo-ls/workspace-meet/context.js')
const meet = lazySharedLibPage(
  _meetCtx,
  () => import('@symbo-ls/workspace-meet/pages/index.js').then((m) => m.meetHome),
  (el) => ({ title: el.call('pageTitle', 'workspace.page.meet') })
)
const meetGuest = lazySharedLibPage(
  _meetCtx,
  () => import('@symbo-ls/workspace-meet/pages/guest.js').then((m) => m.guest),
  (el) => ({ title: el.call('pageTitle', 'workspace.page.meet') })
)

// ── System ── (bundle-split T1: /admin/system + /features + /tickets/status
// lazy — operator surfaces, route-map-only. flags stays static: tiny, and its
// flagsRedirect participates in early routing.)
const system = lazyPage(
  () => import('./system.js').then((m) => m.system),
  (el) => ({
    title: el.call('pageTitle', 'workspace.page.systemHealth')
  })
)
const systemStatusRedirect = lazyPage(
  () => import('./system.js').then((m) => m.systemStatusRedirect),
  (el) => ({ title: el.call('pageTitle', 'workspace.page.systemHealth') })
)
const features = lazyPage(
  () => import('./features.js').then((m) => m.features),
  (el) => ({
    title: el.call('pageTitle', 'workspace.page.features')
  })
)
const ticketsStatus = lazyPage(
  () => import('./features.js').then((m) => m.ticketsStatus),
  (el) => ({
    title: el.call('pageTitle', 'workspace.page.ticketsStatus')
  })
)
import { flags, flagsRedirect } from './flags.js'

// ── Admin (bundle-split T1: lazy per-page, no barrel) ──
// Every admin page loads on first visit via lazyPage — admin is an operator
// surface and has no business in the boot-critical chunk. Two footnotes:
// • bare-keys stay safe: AdminSidebar/AdminUserRow/AdminModule* are registered
//   in components/index.js (which statically imports admin.js + module
//   components), so lazy PAGES mount against an eagerly-registered registry —
//   that pin also means admin.js/module.js themselves dedupe into the eager
//   chunk until the components move to their own files (ticketed follow-up).
// • loaders import the local ./admin/ SOURCE files, never the identity-index
//   barrel — a barrel chunk would re-bundle all 16 pages as one blob.
// • each admin title is a LITERAL metadata function. A `(key) => (el) => …`
//   factory closed over `key`; frank cannot serialize a closure, so the
//   published `metadata` read `key` off the shared globalScope instead (an
//   unrelated helper value) and every admin tab title resolved wrong.
const admin = lazyPage(
  () => import('./admin/admin.js').then((m) => m.admin),
  (el) => ({
    title: el.call('pageTitle', 'admin.page.title')
  })
)
const adminTeams = lazyPage(
  () => import('./admin/teams.js').then((m) => m.adminTeams),
  (el) => ({ title: el.call('pageTitle', 'admin.page.teams') })
)
const adminPermissions = lazyPage(
  () => import('./admin/permissions.js').then((m) => m.adminPermissions),
  (el) => ({ title: el.call('pageTitle', 'admin.page.permissions') })
)
const adminActivityScoring = lazyPage(
  () => import('./admin/activity-scoring.js').then((m) => m.adminActivityScoring),
  (el) => ({ title: el.call('pageTitle', 'admin.page.activityScoring') })
)
const adminDanger = lazyPage(
  () => import('./admin/danger.js').then((m) => m.adminDanger),
  (el) => ({ title: el.call('pageTitle', 'admin.page.danger') })
)
const adminLogs = lazyPage(
  () => import('./admin/logs.js').then((m) => m.adminLogs),
  (el) => ({ title: el.call('pageTitle', 'admin.page.logs') })
)
const adminIntegrations = lazyPage(
  () => import('./admin/integrations.js').then((m) => m.adminIntegrations),
  (el) => ({ title: el.call('pageTitle', 'admin.page.integrations') })
)
const adminIntegrationsGithub = lazyPageWithComponents(
  () => import('./admin/integrations-github.js'),
  'adminIntegrationsGithub',
  (el) => ({ title: el.call('pageTitle', 'admin.page.integrationsGithub') })
)
const adminIntegrationsGoogle = lazyPageWithComponents(
  () => import('./admin/integrations-google.js'),
  'adminIntegrationsGoogle',
  (el) => ({ title: el.call('pageTitle', 'admin.page.integrationsGoogle') })
)
const adminIntegrationsNpm = lazyPageWithComponents(
  () => import('./admin/integrations-npm.js'),
  'adminIntegrationsNpm',
  (el) => ({ title: el.call('pageTitle', 'admin.page.integrationsNpm') })
)
const adminIntegrationsGeneric = lazyPage(
  () => import('./admin/integrations-generic.js').then((m) => m.adminIntegrationsGeneric),
  (el) => ({ title: el.call('pageTitle', 'admin.page.integrationsGeneric') })
)
const adminIntegrationsMcp = lazyPageWithComponents(
  () => import('./admin/integrations-mcp.js'),
  'adminIntegrationsMcp',
  (el) => ({ title: el.call('pageTitle', 'admin.page.integrationsMcp') })
)
const adminStatus = lazyPage(
  () => import('./admin/status.js').then((m) => m.adminStatus),
  (el) => ({ title: el.call('pageTitle', 'admin.page.status') })
)
const adminDomains = lazyPage(
  () => import('./admin/domains.js').then((m) => m.adminDomains),
  (el) => ({ title: el.call('pageTitle', 'admin.page.domains') })
)
// /admin/branding — the ORG's white-label presentation (name, tagline, the two
// marks, the three colours, the boot theme and the pre-auth language). The
// sibling of /admin/domains: that page attaches the branded HOST, this one
// says what the branded surfaces on it look like.
const adminBranding = lazyPage(
  () => import('./admin/branding.js').then((m) => m.adminBranding),
  (el) => ({ title: el.call('pageTitle', 'admin.page.branding') })
)
// /admin/data — the workspace data export ("export any time"): an owner or
// admin starts a ZIP of the whole workspace, downloads it through a
// short-lived signed link, deletes it. Server: /workspaces/:id/exports.
const adminData = lazyPage(
  () => import('./admin/data.js').then((m) => m.adminData),
  (el) => ({ title: el.call('pageTitle', 'admin.data.page.title') })
)
const adminUsage = lazyPage(
  () => import('./admin/usage.js').then((m) => m.adminUsage),
  (el) => ({ title: el.call('pageTitle', 'admin.page.usage') })
)
const adminAnalytics = lazyPage(
  () => import('./admin/analytics.js').then((m) => m.adminAnalytics),
  (el) => ({ title: el.call('pageTitle', 'admin.page.analytics') })
)
const adminDashboardDefaults = lazyPage(
  () => import('./admin/dashboard-defaults.js').then((m) => m.adminDashboardDefaults),
  (el) => ({ title: el.call('pageTitle', 'admin.page.dashboardDefaults') })
)
// /admin/workspace-settings — the GENERAL workspace settings page (the
// workspace default language today; the home for every workspace-level
// preference that is not a specialised console of its own).
const adminWorkspaceSettings = lazyPage(
  () => import('./admin/workspace-settings.js').then((m) => m.adminWorkspaceSettings),
  (el) => ({ title: el.call('pageTitle', 'admin.page.workspaceSettings') })
)
// /admin/mail — the workspace mail console (architecture/MAIL.md §3.5). A
// SHARED-LIB page loader, not lazyPage: the page's cards and data plane live
// in @symbo-ls/workspace-mail (MailAdminProviderCard, MailSetupStep,
// loadMailSetup, …), so the mail context must fold BEFORE the page mounts
// for those bare keys to resolve — same fold the '/mail' route runs.
const adminMail = lazySharedLibPage(
  () => import('@symbo-ls/workspace-mail/context.js'),
  () => import('./admin/mail.js').then((m) => m.adminMail),
  (el) => ({ title: el.call('pageTitle', 'admin.mail.page.title') })
)
// /admin/module — the Client Module console (connect DB, grant tables, assign
// module+version, personalize, rollback). Local page (workspace-shell owns it).
const adminModule = lazyPageWithComponents(
  () => import('./admin/module.js'),
  'adminModule',
  (el) => ({ title: el.call('pageTitle', 'admin.page.module') })
)
import { sessionBridge } from './session-bridge.js'
import { signInSession } from './signInSession.js'
// /demo — branded email gate shown to unauthed visitors on demo.symbols.app /
// dev.demo.symbols.app (and local `?demo=1` / symbols_force_demo override).
// redirectIfUnauthed bounces guests here instead of /login when isDemoHost().
// Lazy (T1): only demo hosts ever mount it; the loader covers the beat.
const demoGate = lazyPage(
  () => import('./demo.js').then((m) => m.demoGate),
  (el) => ({
    title: el.call('polyglot', 'workspace.page.demo')
  })
)
// '/*' catch-all — replaces canvas's own `'/*': main` (dropped from the
// merged route map in context.js). See functions/canvasProjectRedirect.js +
// pages/projectRedirect.js (tickets/workspace.md ASK-USER "should the
// workspace shell serve project CANVAS routes at all?", option 1).
import { projectRedirect } from './projectRedirect.js'

export default {
  // Intranet
  '/': main,
  // Bare `/w` (no org slug) — the workspace hub: auto-forwards to the only
  // org, or shows the picker. Was an unrouted 404 (2026-07-29 per Nika).
  '/w': workspaceHub,
  '/team-members': people,
  // Legacy alias — old links/bookmarks keep working; nav points at
  // /team-members now (renamed 2026-06-06 per Nika).
  '/people': people,
  // Contacts directory moved to the system/crm-app marketplace module
  // (correct Manifest v2 shape) — /contacts previously lived here, baked
  // directly into the shell (the greenig-in-shell anti-pattern).
  '/my': my,
  '/announcements': announcements,
  '/company': company,
  // (The legacy static '/claims' route is gone — WS6: GreenIG's pages inject
  // dynamically from its published v2 module, scoped to its own workspace.)
  '/add-app': addApp,
  '/apps': appsHub,
  '/apps/store': appsStore,
  // Its own title: a pane titles itself from the ROUTE def's metadata, and a
  // string-extend def carries none until it renders — the pane bar read the
  // raw path "/team-and-agents" (measured live 2026-09-22).
  '/team-and-agents': {
    extends: 'teamAndAgents',
    metadata: (el) => ({ title: el.call('pageTitle', 'workspace.page.teamAndAgents') })
  },
  '/agents': agents,
  '/my-agents': agents, // back-compat alias → /agents

  // Analytics surface — tabs share AnalyticsNavbar. Registered here as lazy
  // routes (lazySharedLib folds @symbo-ls/workspace-analytics' context in on
  // first mount) now that analytics is deferred out of the eager sharedLibraries
  // merge. Canonical + legacy alias paths mirror the analytics package's own
  // pages map (pages/index.js + pages/logs/index.js). Bare /logs stays a shell
  // redirect so existing deep-links/bookmarks don't 404 after the route move.
  '/analytics': analyticsProject,
  '/analytics/map': analyticsMap,
  '/analytics/sessions': analyticsSessions,
  '/analytics/sessions/:id': analyticsSession,
  '/analytics/session': analyticsSessionFixed,
  '/analytics/visitors': analyticsVisitors,
  '/analytics/bugs': analyticsBugs,
  '/analytics/events': analyticsEvents,
  '/analytics/logs/session': analyticsLogsSessionFixed,
  '/workspace-analytics/logs/session': analyticsLogsSessionFixed,
  '/workspace-analytics': workspaceAnalytics,
  '/workspace-analytics/map': analyticsMap,
  '/workspace-analytics/logs': analyticsLogs,
  '/workspace-analytics/logs/users': analyticsLogsUsers,
  '/workspace-analytics/logs/sessions': analyticsLogsSessions,
  '/workspace-analytics/logs/sessions/:id': analyticsLogsSession,
  '/workspace-analytics/logs/events': analyticsLogsEvents,
  '/workspace-analytics/logs/bugs': analyticsLogsBugs,
  '/workspace-analytics/logs/network': analyticsLogsNetwork,
  '/workspace-analytics/logs/verbose': analyticsLogsVerbose,
  // Legacy /analytics/logs/* aliases (kept so deep-links/bookmarks don't 404).
  '/analytics/logs': analyticsLogs,
  '/analytics/logs/users': analyticsLogsUsers,
  '/analytics/logs/sessions': analyticsLogsSessions,
  '/analytics/logs/sessions/:id': analyticsLogsSession,
  '/analytics/logs/events': analyticsLogsEvents,
  '/analytics/logs/bugs': analyticsLogsBugs,
  '/analytics/logs/network': analyticsLogsNetwork,
  '/analytics/logs/verbose': analyticsLogsVerbose,
  // Even older bare /logs/* aliases. Bare /logs itself stays the redirect.
  '/logs': logsRedirect,
  '/logs/users': analyticsLogsUsers,
  '/logs/sessions': analyticsLogsSessions,
  '/logs/sessions/:id': analyticsLogsSession,
  '/logs/events': analyticsLogsEvents,
  '/logs/bugs': analyticsLogsBugs,
  '/logs/network': analyticsLogsNetwork,
  '/logs/verbose': analyticsLogsVerbose,
  // Signed-in user's own account settings, under its own tab title
  // (accountSettings above). `/profile` stays as a
  // legacy alias for `?u=<email>` cross-user links still in flight.
  // `/orgs` lands on the same account surface — historically the
  // membership picker, now folded into the unified account page.
  '/settings': accountSettings,
  '/profile': profile,
  '/orgs': profile,
  // Public-facing user profile — both prefixes mount the same page
  // (the slug is read from the URL inside the page's onCreate). The
  // canonical link surfaces `/user/:slug` so it reads cleanly when
  // shared; `/u/:slug` is a compact alias that the marketplace and
  // feed components prefer.
  '/user/:slug': userProfile,
  '/u/:slug': userProfile,
  // /s/:token — the recipient's view of a public share link
  // (PUBLIC-SHARE-LINKS-1). A guest route: listed in the shell entry's
  // publicPathPrefixes so an unauthed visitor renders it instead of
  // bouncing to /login. Lazy — nothing else in the app references it, and
  // the surface is reached by URL only.
  '/s/:token': sharedResource,
  // demo.symbols.app email gate — a guest route (in AUTH_PATHS +
  // publicPathPrefixes) so chrome stays hidden and no auth bounce fires.
  '/demo': demoGate,
  '/login': login,
  '/signin': signin,
  '/signup': signup,
  '/signup-confirmation': signupConfirmation,
  '/reset-password': resetPassword,
  '/auth/callback': authCallback,
  '/github-auth-callback': authCallback,
  '/auth/magic-callback': magicCallback,
  // Emailed invitation landings (see the acceptInvite/waitlistInvite defs
  // above). One page, four kinds — the path names the kind.
  '/accept-invite': acceptInvite,
  '/accept-org-invite': acceptInvite,
  '/accept-team-invite': acceptInvite,
  '/accept-workspace-invite': acceptInvite,
  '/waitlist-invite': waitlistInvite,
  '/data': data,
  '/data/projects': data,
  '/data/users': data,
  '/data/plans': data,
  '/data/organizations': data,
  '/data/teams': data,
  '/data/subscriptions': data,
  '/data/external': dataExternal,

  // Calendar
  '/calendar': calendar,

  // Reminders — built-in personal reminders app (workspace-shell page, not
  // a feature package).
  '/reminders': reminders,
  // History — built-in timeline of the workspace activity log.
  '/history': history,
  // Getting started — the first-thread offers (POB S9/S10). Not under
  // `/onboarding`: that is the old wizard's route.
  '/getting-started/team': gettingStartedTeam,
  '/getting-started/imports': gettingStartedImports,
  '/inbox': inbox,

  // Tasks (né Tickets — renamed 2026-08-06 per Nika "in workspace — rename
  // tickets to tasks … instead of /tickets it will be /tasks"). /tasks is the
  // board kanban canvas (2026-06-09 per Nika); the personalized "My view"
  // lives at /tasks-page; /tasks/canvas removed. USER-FACING rename only —
  // the data plane keeps its internal names (sdk.tickets.*, root.tickets,
  // @symbo-ls/workspace-tickets, server /core/tickets).
  '/tasks': ticketsBoard,
  '/tasks-page': tickets,
  // Dedicated full-page view (linked from the modal's Expand icon).
  // Path-param shape mirrors `/user/:slug` — the smbls auto-router
  // matches it after the explicit /tasks/* routes are tried.
  '/task/:id': ticketView,
  '/tasks/general': ticketsGeneral,
  // /tasks/feed is the summary digest (recent activity + standups).
  '/tasks/feed': ticketsFeed,
  // Epics + Stories merged into one view (2026-06-09 per Nika).
  '/tasks/epics': ticketsEpics,
  '/tasks/board': ticketsBoard,
  '/tasks/status': ticketsStatus,
  '/tasks/standups': ticketsStandups,
  '/tasks/release': ticketsRelease,
  '/tasks/roadmap': ticketsRoadmap,

  // Legacy /tickets* + /ticket/:id — URL-NORMALIZING REDIRECTS, not aliases
  // (2026-08-06 per Nika "you dont have to alias /tickets — fix all
  // references to /tasks"). One shared def computes its own canonical
  // target via canonicalRoutePath, so old deep-links/bookmarks/stored
  // navbar configs land on /tasks (or /resolutions for the moved groups)
  // with the address bar normalized. No page content is ever served under
  // a /tickets URL again.
  '/tickets': tasksRedirect,
  '/tickets-page': tasksRedirect,
  '/ticket/:id': tasksRedirect,
  '/tickets/general': tasksRedirect,
  '/tickets/feed': tasksRedirect,
  '/tickets/epics': tasksRedirect,
  '/tickets/board': tasksRedirect,
  '/tickets/status': tasksRedirect,
  '/tickets/standups': tasksRedirect,
  '/tickets/release': tasksRedirect,
  '/tickets/roadmap': tasksRedirect,

  // Resolutions — dedicated top-level surface (own sidebar). The
  // Requests + Resolutions groups moved here out of the tickets sidebar.
  '/resolutions': resolutions,
  '/resolutions/improvements': resolutionsImprovements,
  '/resolutions/edit-requests': resolutionsEditRequests,
  '/resolutions/audit': requestsAudit,
  '/resolutions/requests': requests,
  '/resolutions/requests/inbound': requestsInbound,
  '/resolutions/requests/outbound': requestsOutbound,

  // Legacy /tickets/{requests,resolutions}* — same URL-normalizing redirect
  // (canonicalRoutePath maps these to their /resolutions homes).
  '/tickets/requests': tasksRedirect,
  '/tickets/requests/inbound': tasksRedirect,
  '/tickets/requests/outbound': tasksRedirect,
  '/tickets/requests/audit': tasksRedirect,
  '/tickets/resolutions': tasksRedirect,
  '/tickets/resolutions/improvements': tasksRedirect,
  '/tickets/resolutions/edit-requests': tasksRedirect,

  // Chat
  '/chat': chat,

  // Mail
  '/mail': mail,

  // Notes (renamed from Documents 2026-08-06 per Nika — the /documents*
  // family + /docs are URL-normalizing redirects via canonicalRoutePath,
  // same pattern as /tickets → /tasks above)
  '/notes': documents,
  '/notes/knowledge-base': documentsKb,
  '/notes/kb': documentsKb,
  '/notes/editor': documentsEditor,
  '/docs': notesRedirect,
  '/documents': notesRedirect,
  '/documents/notes': notesRedirect,
  '/documents/knowledge-base': notesRedirect,
  '/documents/kb': notesRedirect,
  '/documents/editor': notesRedirect,

  // Files — shared desktop canvas (real uploads + folders + markdown docs)
  '/files': files,

  // Infra — deployment canvas + status page. Lazy shared library (deferred out
  // of the eager sharedLibraries merge); registers its components/functions on
  // first mount. /infra/status also opens in the shell PageSlot via
  // root._canvasSlot (resolved from this same registry).
  '/infra': infra,
  '/infra/status': infraStatus,

  // Meet
  '/meet': meet,
  '/meet-guest': meetGuest,

  // Marketplace — lazy shared library (folds the full marketplace context on
  // first mount; the eager chunk carries only the shell-root install-modal
  // slice — see workspace/context.js #16). These override the dead canvas
  // `/marketplace` page (canvasPages['/marketplace'], superseded here since
  // shellPages wins the context.js pages merge). `/feed` aliases the community
  // feed, mirroring marketplace's own page map.
  '/marketplace': marketplace,
  '/marketplace/feed': marketplaceFeed,
  '/feed': marketplaceFeed,
  '/marketplace/installable-rail-demo': marketplaceRailDemo,
  '/marketplace/prompt-gallery': marketplacePromptGallery,

  // Preview — lazy shared library (folds the full preview context on first
  // mount; the eager chunk carries only the Simone-Chat picker/model backstop
  // fns — see workspace/context.js #16). Replaces the previously boot-merged
  // previewPages['/preview'] (raw eager page in context.js). The home/canvas
  // PageSlot opens the same page via _APP_WINDOW_PAGES._previewPage (main.js),
  // which folds this same context first.
  '/preview': preview,

  // System Health — canonical route is `/system-status`. The legacy
  // bare `/system` registration was removed because it shadowed the
  // canvas project route `/system/:slug` (e.g. `/system/landing`
  // should open the "system" org's "landing" project in canvas, but
  // was being intercepted by the smbls prefix-matcher and rendered
  // the System Health page instead). Anyone still navigating to bare
  // `/system` will fall through to canvas's `/*` handler which 404s
  // gracefully — and we've updated every internal link to point at
  // `/system-status` directly (see AppNavbar TITLES below).
  '/system-status': systemStatusRedirect,
  '/admin/system': system,
  '/features': features,
  '/flags': flagsRedirect,
  '/admin/flags': flags,

  // Top-level aliases — sidebar links sometimes use bare /integrations and
  // /system instead of the /admin/* nested paths. Both must be registered
  // so the smbls auto-router does not 404 before dispatchUrl can overlay.
  '/integrations': adminIntegrations,

  // Admin
  '/admin': admin,
  '/admin/teams': adminTeams,
  '/admin/permissions': adminPermissions,
  '/admin/activity-scoring': adminActivityScoring,
  '/admin/logs': adminLogs,
  '/admin/integrations': adminIntegrations,
  '/admin/integrations/github': adminIntegrationsGithub,
  '/admin/integrations/google': adminIntegrationsGoogle,
  '/admin/integrations/npm': adminIntegrationsNpm,
  '/admin/integrations/openai': adminIntegrationsGeneric,
  '/admin/integrations/livekit': adminIntegrationsGeneric,
  '/admin/integrations/google_analytics': adminIntegrationsGeneric,
  '/admin/integrations/n8n': adminIntegrationsGeneric,
  '/admin/integrations/custom_http': adminIntegrationsGeneric,
  // Marketplace-seeded kinds (migration 0063). All use the generic
  // detail page — which reads config_schema.fields from the kind row
  // so no bespoke code per kind. Add to this list AND to 0063 together.
  '/admin/integrations/plausible': adminIntegrationsGeneric,
  '/admin/integrations/posthog': adminIntegrationsGeneric,
  '/admin/integrations/segment': adminIntegrationsGeneric,
  '/admin/integrations/mixpanel': adminIntegrationsGeneric,
  '/admin/integrations/firebase': adminIntegrationsGeneric,
  '/admin/integrations/cloudflare_workers': adminIntegrationsGeneric,
  '/admin/integrations/vercel': adminIntegrationsGeneric,
  '/admin/integrations/aws': adminIntegrationsGeneric,
  '/admin/integrations/postgres': adminIntegrationsGeneric,
  '/admin/integrations/mongodb': adminIntegrationsGeneric,
  '/admin/integrations/planetscale': adminIntegrationsGeneric,
  '/admin/integrations/neon': adminIntegrationsGeneric,
  '/admin/integrations/redis': adminIntegrationsGeneric,
  '/admin/integrations/zapier': adminIntegrationsGeneric,
  '/admin/integrations/make': adminIntegrationsGeneric,
  '/admin/integrations/temporal': adminIntegrationsGeneric,
  '/admin/integrations/auth0': adminIntegrationsGeneric,
  '/admin/integrations/clerk': adminIntegrationsGeneric,
  '/admin/integrations/workos': adminIntegrationsGeneric,
  '/admin/integrations/stripe': adminIntegrationsGeneric,
  '/admin/integrations/paddle': adminIntegrationsGeneric,
  '/admin/integrations/lemonsqueezy': adminIntegrationsGeneric,
  '/admin/integrations/anthropic': adminIntegrationsGeneric,
  '/admin/integrations/replicate': adminIntegrationsGeneric,
  '/admin/integrations/pinecone': adminIntegrationsGeneric,
  '/admin/integrations/slack': adminIntegrationsGeneric,
  '/admin/integrations/discord': adminIntegrationsGeneric,
  '/admin/integrations/twilio': adminIntegrationsGeneric,
  '/admin/integrations/sendgrid': adminIntegrationsGeneric,
  '/admin/integrations/resend': adminIntegrationsGeneric,
  '/admin/integrations/aws_s3': adminIntegrationsGeneric,
  '/admin/integrations/cloudflare_r2': adminIntegrationsGeneric,
  '/admin/integrations/uploadthing': adminIntegrationsGeneric,
  '/admin/integrations/sentry': adminIntegrationsGeneric,
  '/admin/integrations/datadog': adminIntegrationsGeneric,
  '/admin/integrations/logrocket': adminIntegrationsGeneric,
  '/admin/integrations/figma': adminIntegrationsGeneric,
  '/admin/integrations/mcp': adminIntegrationsMcp,
  '/admin/status': adminStatus,
  '/admin/domains': adminDomains,
  '/admin/branding': adminBranding,
  '/admin/usage': adminUsage,
  '/admin/analytics': adminAnalytics,
  '/admin/dashboard-defaults': adminDashboardDefaults,
  '/admin/workspace-settings': adminWorkspaceSettings,
  '/admin/mail': adminMail,
  '/admin/module': adminModule,
  '/admin/data': adminData,
  '/admin/danger': adminDanger,

  // Cross-origin auth helper. Other Symbols subdomains iframe this page
  // to request the live governance Supabase session, so e.g. the canvas
  // app can read RLS-gated integration rows without a separate signin.
  '/_session-bridge': sessionBridge,
  '/sign-in-session': signInSession,

  // Catch-all — MUST stay last / win over canvas's dropped `'/*'` in
  // context.js's merge. Redirects `/<owner>/<key>` project paths to the host
  // that runs the full canvas editor graph; renders a plain not-found for
  // everything else genuinely unmatched.
  '/*': projectRedirect
}
